import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import FormModal from '../components/common/FormModal';
import ConfirmModal from '../components/common/ConfirmModal';
import { toast } from 'react-hot-toast';

const { 
  FiTrendingUp, FiPlus, FiSettings, FiZap, FiTarget, 
  FiBarChart2, FiMoreVertical, FiTrash2, FiEdit3, FiCheckCircle 
} = FiIcons;

export default function GrowthEngine() {
  const [rules, setRules] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDelete, setShowDelete] = useState(null);
  const [editingRule, setEditingRule] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    trigger_type: 'course_completion',
    trigger_value: '',
    suggested_course_id: '',
    priority: 0,
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rulesRes, coursesRes] = await Promise.all([
        supabase.from('suggestion_rules_2024').select(`
          *,
          suggested_course:courses_2024(title)
        `).order('priority', { ascending: false }),
        supabase.from('courses_2024').select('id, title').eq('status', 'Published')
      ]);

      if (rulesRes.error) throw rulesRes.error;
      if (coursesRes.error) throw coursesRes.error;

      setRules(rulesRes.data || []);
      setCourses(coursesRes.data || []);
    } catch (error) {
      toast.error('Failed to load growth data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRule) {
        const { error } = await supabase
          .from('suggestion_rules_2024')
          .update(formData)
          .eq('id', editingRule.id);
        if (error) throw error;
        toast.success('Rule updated successfully');
      } else {
        const { error } = await supabase
          .from('suggestion_rules_2024')
          .insert([formData]);
        if (error) throw error;
        toast.success('Suggestion rule created');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('suggestion_rules_2024')
        .delete()
        .eq('id', showDelete);
      if (error) throw error;
      toast.success('Rule deleted');
      setShowDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Deletion failed');
    }
  };

  const triggerOptions = [
    { value: 'course_completion', label: 'Course Completion', icon: FiCheckCircle },
    { value: 'tag_added', label: 'Specific Tag Added', icon: FiTarget },
    { value: 'enrollment', label: 'New Enrollment', icon: FiZap },
    { value: 'manual', label: 'Manual/Always Show', icon: FiSettings }
  ];

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-12 pb-24">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="space-y-2">
          <div className="flex items-center gap-3 text-indigo-500 mb-2">
            <div className="p-2 bg-indigo-500/10 rounded-lg">
              <SafeIcon icon={FiTrendingUp} className="text-xl" />
            </div>
            <span className="text-[10px] uppercase tracking-[0.2em] font-black">Conversion Engine</span>
          </div>
          <h1 className="text-5xl font-black text-white tracking-tighter uppercase italic">
            Growth <span className="text-indigo-500">Engine</span>
          </h1>
          <p className="text-slate-400 font-medium max-w-md">
            Automate your up-sell and cross-sell strategy with dynamic course suggestions based on student behavior.
          </p>
        </div>
        <button 
          onClick={() => {
            setEditingRule(null);
            setFormData({ title: '', trigger_type: 'course_completion', trigger_value: '', suggested_course_id: '', priority: 0, is_active: true });
            setShowModal(true);
          }}
          className="bg-white text-black px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-500 hover:text-white transition-all flex items-center gap-2"
        >
          <SafeIcon icon={FiPlus} /> Create Rule
        </button>
      </div>

      {/* Analytics Mini-Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: 'Total Suggestions', value: '1.2k', sub: '+12% this month', color: 'indigo' },
          { label: 'Conversion Rate', value: '8.4%', sub: 'Target: 10%', color: 'emerald' },
          { label: 'Growth Revenue', value: '$12,450', sub: 'From suggestions', color: 'amber' },
        ].map((stat, i) => (
          <div key={i} className="bg-white/[0.02] border border-white/10 p-8 rounded-[2.5rem] relative overflow-hidden group">
            <div className={`absolute top-0 right-0 w-32 h-32 bg-${stat.color}-500/10 blur-3xl group-hover:bg-${stat.color}-500/20 transition-all`} />
            <p className="text-[10px] uppercase tracking-widest font-black text-slate-500 mb-4">{stat.label}</p>
            <h3 className="text-4xl font-black text-white mb-2">{stat.value}</h3>
            <p className={`text-[10px] font-bold text-${stat.color}-500 uppercase tracking-wider`}>{stat.sub}</p>
          </div>
        ))}
      </div>

      {/* Rules List */}
      <div className="space-y-6">
        <h2 className="text-xl font-black text-white uppercase tracking-tight flex items-center gap-2">
          <SafeIcon icon={FiZap} className="text-indigo-500" /> Active Strategies
        </h2>

        <div className="grid gap-4">
          <AnimatePresence mode="popLayout">
            {rules.map((rule) => (
              <motion.div
                key={rule.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white/[0.02] border border-white/5 hover:border-white/20 p-6 rounded-3xl group transition-all flex items-center justify-between"
              >
                <div className="flex items-center gap-6">
                  <div className="w-12 h-12 bg-indigo-500/10 rounded-2xl flex items-center justify-center text-indigo-500 border border-indigo-500/20">
                    <SafeIcon icon={triggerOptions.find(t => t.value === rule.trigger_type)?.icon || FiTarget} className="text-xl" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold uppercase text-[12px] tracking-wider">{rule.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-slate-500 uppercase font-black">Suggests:</span>
                      <span className="text-[10px] text-indigo-400 font-bold uppercase tracking-widest">{rule.suggested_course?.title}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-8">
                  <div className="text-right hidden md:block">
                    <p className="text-[10px] text-slate-500 uppercase font-black mb-1">Priority</p>
                    <span className="text-sm font-mono text-white">{rule.priority}</span>
                  </div>
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => {
                        setEditingRule(rule);
                        setFormData({
                          title: rule.title,
                          trigger_type: rule.trigger_type,
                          trigger_value: rule.trigger_value,
                          suggested_course_id: rule.suggested_course_id,
                          priority: rule.priority,
                          is_active: rule.is_active
                        });
                        setShowModal(true);
                      }}
                      className="p-3 hover:bg-white/10 rounded-xl text-slate-400 hover:text-white transition-all"
                    >
                      <SafeIcon icon={FiEdit3} />
                    </button>
                    <button 
                      onClick={() => setShowDelete(rule.id)}
                      className="p-3 hover:bg-rose-500/20 rounded-xl text-slate-400 hover:text-rose-500 transition-all"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {rules.length === 0 && !loading && (
            <div className="text-center py-20 bg-white/[0.01] border border-dashed border-white/10 rounded-[3rem]">
              <div className="w-16 h-16 bg-white/5 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiTarget} className="text-2xl text-slate-500" />
              </div>
              <h3 className="text-white font-black uppercase text-[10px] tracking-[0.2em] mb-2">No Growth Rules</h3>
              <p className="text-slate-500 text-[12px] font-medium">Start by creating your first automated suggestion.</p>
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      <FormModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingRule ? "Edit Strategy" : "New Growth Strategy"}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-2 block">Strategy Name</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:outline-none focus:border-indigo-500 transition-all"
                placeholder="e.g. Advanced Upsell for Basics Completion"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-2 block">Trigger Type</label>
                <select
                  value={formData.trigger_type}
                  onChange={(e) => setFormData({ ...formData, trigger_type: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:outline-none focus:border-indigo-500 transition-all appearance-none"
                >
                  {triggerOptions.map(opt => (
                    <option key={opt.value} value={opt.value} className="bg-[#0a0a0a]">{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-2 block">Priority (Higher = Sooner)</label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:outline-none focus:border-indigo-500 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-2 block">Suggested Course</label>
              <select
                required
                value={formData.suggested_course_id}
                onChange={(e) => setFormData({ ...formData, suggested_course_id: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:outline-none focus:border-indigo-500 transition-all appearance-none"
              >
                <option value="" className="bg-[#0a0a0a]">Select Course</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id} className="bg-[#0a0a0a]">{course.title}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-5 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all shadow-xl shadow-indigo-500/20"
          >
            {editingRule ? "Save Changes" : "Deploy Strategy"}
          </button>
        </form>
      </FormModal>

      <ConfirmModal
        isOpen={!!showDelete}
        onClose={() => setShowDelete(null)}
        onConfirm={handleDelete}
        title="Archive Strategy?"
        message="This rule will no longer be applied to student dashboards. Conversion tracking will be preserved."
      />
    </div>
  );
}